import axios, { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { withAuthRegionFailover } from '@/auth';
import { ENABLE_API_MOCK } from '@/constants/env.constants';
import { buildSignInUrl } from '@/utils/helpers/auth-redirect.helpers';

enum CommonHeader {
  Authorization = 'authorization',
  // ...
}

export const apiBaseUrl = import.meta.env.VITE_API_ORIGIN ?? window.origin;
const isMockedApi = ENABLE_API_MOCK === 'true';

const SENSITIVE_HEADERS = ['authorization'];

const sanitizeHeaders = (headers: Record<string, unknown>) => {
  const sanitized = { ...headers };
  Object.keys(sanitized).forEach((key) => {
    if (SENSITIVE_HEADERS.includes(key.toLowerCase())) {
      sanitized[key] = '[REDACTED]';
    }
  });
  return sanitized;
};

const sendErrorToSentry = (error: AxiosError) => {
  if (error.code === 'ERR_CANCELED') return;

  Sentry.withScope((scope) => {
    if (error.response) {
      scope.setTag('status', error.response.status);
      scope.setExtra('responseData', error.response.data);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const headers = error.response.headers?.toJSON?.() ?? error.response.headers;
      scope.setExtra('headers', sanitizeHeaders(headers));
    }

    if (error.config) {
      scope.setExtra('url', error.config.url);
      scope.setExtra('method', error.config.method);
      scope.setExtra('data', error.config.data);
      scope.setExtra('params', error.config.params);
    }

    Sentry.captureException(error);
  });
};

const refreshTokenErrorHandler = (error: Error | unknown) => {
  // eslint-disable-next-line
  console.error('Token refresh failed:', error);
  localStorage.removeItem('token');
  localStorage.removeItem('userId');

  const { pathname, search } = globalThis.location;
  globalThis.location.href = buildSignInUrl(pathname, search);
};

const getHttpInstance = () => {
  const instance = axios.create({
    baseURL: isMockedApi ? `${apiBaseUrl}/api` : apiBaseUrl,
  });

  instance.interceptors.request.use(async (request) => {
    const isExternalRequest = request.url?.includes('https://');

    if (!isExternalRequest) {
      // A token refresh aimed at a Region that has just failed over would sign the
      // user out; one re-resolve and retry covers the health-check detection window.
      const authSession = await withAuthRegionFailover(() => fetchAuthSession());
      const accessToken = authSession.tokens?.accessToken.toString();

      if (accessToken) {
        request.headers[CommonHeader.Authorization] = accessToken;
        localStorage.setItem('token', accessToken);
      }

      const tenantId = localStorage.getItem('selectedOrganizationId');
      const tenantPrefix = localStorage.getItem('selectedOrganizationPrefix');
      if (tenantId) {
        request.headers['X-Tenant-Id'] = tenantPrefix ? `${tenantPrefix}:${tenantId}` : tenantId;
      }
    }

    return request;
  });

  instance.interceptors.response.use(
    (response) => response, // Directly return successful responses.
    async (error) => {
      const originalRequest = error.config;
      if (!originalRequest) {
        sendErrorToSentry(error);
        return Promise.reject(error);
      }
      const isExternalRequest = originalRequest.url?.includes('https://');
      // eslint-disable-next-line no-underscore-dangle
      if (!isExternalRequest && error.response?.status === 401 && !originalRequest._retry) {
        // eslint-disable-next-line no-underscore-dangle
        originalRequest._retry = true; // Mark the request as retried to avoid infinite loops.
        localStorage.removeItem('streamToken');
        localStorage.removeItem('streamChannelGrants');
        try {
          const authSession = await fetchAuthSession({ forceRefresh: true });
          const accessToken = authSession.tokens?.accessToken.toString();

          if (accessToken) {
            // Update the authorization header with the new access token.
            instance.defaults.headers.common[CommonHeader.Authorization] = accessToken;
            localStorage.setItem('token', accessToken);
            return await instance(originalRequest); // Retry the original request with the new access token.
          }

          refreshTokenErrorHandler(new Error('Access token not found'));
        } catch (refreshError) {
          // Handle refresh token errors by clearing stored tokens and redirecting to the login page.
          refreshTokenErrorHandler(refreshError);
          return Promise.reject(refreshError);
        }
      }
      sendErrorToSentry(error);
      return Promise.reject(error); // For all other errors, return the error as is.
    },
  );

  return instance;
};

export const http = getHttpInstance();
